import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { doRequest } from '@r1-frontend/do-request';
import { addUnconfirmedEmail } from '@r1-frontend/api-domru/api-profile/v1/contacts/add-unconfirmed-email';
import { confirmEmail } from '@r1-frontend/api-domru/api-profile/v1/contacts/confirm-email';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import Input from '@r1-frontend/ui-react/components/input';
import GridContainerUI from '@r1-frontend/ui-react/components/layouts/gridContainer';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';

import { POLICY_PAGE_LINK } from '~/src/constants';
import { TState } from '~/src/store';
import { getClientContacts } from '~/src/store/actions/auth/getClientContacts';
import { selectClientContactsEmailConfirmed } from '~/src/store/selectors/contacts';

import { EPopupSteps, Popup } from './popup';

import * as ST from './styled';

interface IProps {
    mainText: string,
}

interface IEmail {
    value: string,
    isValid: boolean,
}

const TEXT = 'Оставьте почту, чтобы быть в курсе событий';
const ERROR_WRONG_QUERY_PARAMS = 'ERROR_PARAMETER';
const WRONG_QUERY_PARAMS_MESSAGE = 'Возможно ссылка устарела';

const EmailWidget: FC<IProps>  = ({ mainText = TEXT }: IProps) => {

    const emailInput = useRef<Input>(null);

    const isAuth = useSelector((state: TState) => state.auth.isAuth);
    const emailsConfirmed = useSelector((state: TState) => selectClientContactsEmailConfirmed(state));

    const [email, setEmail] = useState<IEmail>({
        value: '',
        isValid: false,
    });
    const [contactId, setContactId] = useState<number>(0);
    const [showWidget, setShowWidget] = useState<boolean>(false);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [popupStep, setPopupStep] = useState<EPopupSteps>(EPopupSteps.addedUnconfirmedEmail);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [eventStartSended, setEventStartSended] = useState<boolean>(false);

    const query = (useRouter()).query;
    const dispatch = useDispatch();

    const fetchQueryParams = async(): Promise<void> => {
        const key = String(query.key);
        const contactId = Number(query.id);
        const response = await doRequest.apiProfile<number>(
            confirmEmail({
                key,
                contact_id: contactId,
            }),
        );

        if (response.isSuccess) {
            await emailConfirmedHandler();
        } else {
            dataLayerPush({
                'event' : 'UAevent',
                'category': 'email_collection_widget',
                'action' : 'error',
            });
            setPopupStep(EPopupSteps.error);
            if (response.error.code === ERROR_WRONG_QUERY_PARAMS) {
                setErrorMessage(WRONG_QUERY_PARAMS_MESSAGE);
            } else {
                setErrorMessage(response.error.message ? response.error.message : '');
            }
        }
        setShowPopup(true);
    };

    useEffect(() => {
        if (isAuth &&
            String(query.type) === 'confirm' &&
            query.id &&
            query.key) {
            fetchQueryParams();
        }
    }, []);

    // отображаем виджет только авторизованному пользователю если на договоре нет подтвержденной почты
    useEffect(() => {
        if (isAuth && emailsConfirmed.length === 0) {
            setShowWidget(true);
            dataLayerPush({
                'event' : 'UAevent',
                'category': 'email_collection_widget',
                'action' : 'show_form',
            });
        } else {
            setShowWidget(false);
        }
    }, [isAuth, emailsConfirmed]);

    const changeEmail = useCallback((value: string): void => {
        if (!eventStartSended) {
            dataLayerPush({
                'event': 'UAevent',
                'category': 'email_collection_widget',
                'action': 'started',
            });
            setEventStartSended(true);
        }
        const { current } = emailInput;
        const isValid = current ? !current?.state.hasError : false;
        setEmail({
            value,
            isValid,
        });
    }, [eventStartSended]);

    const onClosePopup = useCallback((): void => {
        setShowPopup(false);
        if (popupStep === EPopupSteps.success) {
            setShowWidget(false);
        }
    }, [popupStep]);

    const submit = useCallback(async(): Promise<void> => {
        const url = location.origin + location.pathname;
        const resp = await doRequest.apiProfile<number>(
            addUnconfirmedEmail({
                email: email.value,
                url,
            }),
        );

        if (resp.isSuccess) {
            dataLayerPush({
                'event' : 'UAevent',
                'category': 'email_collection_widget',
                'action' : 'added_email',
            });
            setContactId(resp.payload);
            setPopupStep(EPopupSteps.addedUnconfirmedEmail);
            setShowPopup(true);
        } else {
            dataLayerPush({
                'event' : 'UAevent',
                'category': 'email_collection_widget',
                'action' : 'error',
            });
            setErrorMessage(resp.error.message);
            setPopupStep(EPopupSteps.error);
            setShowPopup(true);
        }
    }, [email]);

    const emailConfirmedHandler = async(): Promise<void> => {
        dataLayerPush({
            'event' : 'UAevent',
            'category': 'email_collection_widget',
            'action' : 'сonfirmed_email',
        });
        setPopupStep(EPopupSteps.success);
        await dispatch(getClientContacts());
    };

    const buttonText = 'Подписаться';

    return <>
        { showWidget &&
            <GridContainerUI>
                <ST.Container data-test='emailWidget'>
                    <ST.MainText dangerouslySetInnerHTML={{ __html: mainText }} data-test='mainText'/>
                    <ST.EmailInput
                        ref={emailInput}
                        type='email'
                        errorMessage='Введите корректный e-mail'
                        placeholder='Ваш email'
                        onChange={changeEmail}
                        value={email.value}
                        required={true}
                        data-test='emailInput'
                    />
                    <ST.ButtonBlock>
                        <ST.Button
                            type={btnTypes.ACCENT}
                            onClick={submit}
                            disabled={!email.isValid}
                            data-test='subscribeButton'
                        >
                            {buttonText}
                        </ST.Button>
                    </ST.ButtonBlock>
                    <PolicyNote
                        data-test='agreement'
                        buttonText={buttonText}
                        agreeUrl={POLICY_PAGE_LINK}
                    />
                </ST.Container>
            </GridContainerUI>
        }
        <Popup
            email={email.value}
            errorMessage={errorMessage}
            showPopup={showPopup}
            popupStep={popupStep}
            onClosePopup={onClosePopup}
            contactId={contactId}
            setPopupStep={setPopupStep}
            setErrorMessage={setErrorMessage}
            emailConfirmedHandler={emailConfirmedHandler}
        />
    </>;
};

export default connect()(EmailWidget);
